/**
 * React Hooks for State Access
 */
import { useCallback } from 'react';
import { appStore } from './store';
import type { AppStore } from './types';
import { STORAGE_KEYS } from '../constants/storage';

/**
 * Select state slice using selector function
 */
export function useAppSelector<T>(selector: (state: AppStore) => T): T {
  return appStore(selector);
}

/**
 * Get current user
 */
export function useAuth() {
  return useAppSelector((state) => state.user);
}

/**
 * Check if user is authorized
 */
export function useIsAuthorized() {
  return useAppSelector((state) => state.isAuthorized);
}

/**
 * Get auth token
 */
export function useToken() {
  return useAppSelector((state) => state.token);
}

/**
 * Get auth actions
 */
export function useAuthActions() {
  const setToken = useCallback((token: string | null) => {
    appStore.getState().setToken(token);
    if(token){
      localStorage.setItem(STORAGE_KEYS.token, token);
    }
  }, []);

  const setUser = useCallback((user: any) => {
    appStore.getState().setUser(user);
  }, []);

  const setIsAuthorized = useCallback((isAuthorized: boolean) => {
    appStore.getState().setIsAuthorized(isAuthorized);
  }, []);

  const reset = useCallback(() => {
    appStore.getState().reset();
  }, []);

  return {
    setToken,
    setUser,
    setIsAuthorized,
    reset,
  };
}

